import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'
import { FunctionComponent, useEffect, useRef } from 'react'
import { format, parseISO } from 'date-fns'
import { Patient } from '../../types'
import ConfirmationModal from '../../components/shared-components/ConfirmationModal'
import PatientForm from './PatientForm'
import { useDeletePatientMutation, useUpdatePatientMutation } from '../../services/patients'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { FetchBaseQueryErrorResult } from '../../services/baseQuery'
import { ServerError } from '../../types/result'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import {
  resetMedicareValidationResult,
  resetDvaValidationResult,
  resetIhiValidationResult,
} from './PatientFormSlice'
import { formatIHIValidate, formatIHIRecord } from './utils'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { getMissingPrescriberMessage } from '../../features/patient/patientAccessScope'

interface UpdatePatientModalProps {
  patient: Patient
  isOpen: boolean
  onClose: () => any
  selectedPrescriber: LimitedPrescriberProfile | null
  isAbleToCreateOrDelete: boolean
}

const UpdatePatientModal: FunctionComponent<UpdatePatientModalProps> = ({
  patient,
  isOpen,
  onClose,
  selectedPrescriber,
  isAbleToCreateOrDelete,
}) => {
  const prescriber = useSelector(({ prescriber }: RootState) => prescriber?.prescriber)
  const effectivePrescriber = selectedPrescriber || (prescriber as LimitedPrescriberProfile)
  const effectivePrescriberId = effectivePrescriber?.id
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)
  const dispatch = useAppDispatch()
  const errorRef = useRef<HTMLDivElement>(null)
  const toast = useToast()
  const tryOrElse = <T, R>(code: () => T, orElse: (err: unknown) => R) => {
    try {
      return code()
    } catch (err) {
      return orElse(err)
    }
  }

  const transformToPatientFormData = (patient: Patient): Patient => {
    let medicareExpire = ''
    if (patient.medicare_valid_to) {
      const medicareValidTo = tryOrElse(
        () => format(parseISO(patient.medicare_valid_to), 'yyyy-MM-dd'),
        () => 'Invalid Date'
      )
      const regex = /^\d{4}-\d{2}/
      const matched = medicareValidTo?.match(regex) ?? [medicareValidTo]
      medicareExpire = matched[0]
    }
    return {
      ...patient,
      medicare_valid_to: medicareExpire,
    }
  }
  const { medicareValidationResult, dvaValidationResult, ihiValidationResult } = useAppSelector(
    (state) => state.patientForm
  )
  const isMedicareAcceptable = formatIHIValidate(medicareValidationResult).isAcceptable
  const isDvaAcceptable = formatIHIValidate(dvaValidationResult).isAcceptable

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: transformToPatientFormData(patient),
  })
  const { handleSubmit, reset, setError, getValues } = methods
  const [updatePatient, updatePatientResult] = useUpdatePatientMutation()
  const [deletePatient, deletePatientResult] = useDeletePatientMutation()

  const updatePatientError = (updatePatientResult.error as FetchBaseQueryErrorResult['error'])
    ?.data as ServerError

  useEffect(() => {
    if (updatePatientError?.error === 'duplicated-ihi-number') {
      errorRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [updatePatientError?.error])

  const onPatientUpdate = async (data: Patient) => {
    if (!isSharedPatientsFeatureOn && !effectivePrescriberId) {
      toast({
        title: 'Cannot update patient',
        description: getMissingPrescriberMessage('updating a patient'),
        status: 'error',
        isClosable: true,
      })
      return
    }

    const newRecord = formatIHIRecord({
      medicareValidationResult,
      dvaValidationResult,
      ihiValidationResult,
    })

    if (!newRecord) {
      const { ihi_number } = getValues()

      if (
        !ihiValidationResult.service_message?.codeField &&
        ihiValidationResult.ihi_status === '' &&
        ihi_number
      ) {
        setError('ihi_number', {
          type: 'manual',
          message: 'Please validate the IHI number',
        })
        return
      }

      toast({
        position: 'top',
        title: 'Cannot update patient',
        description: 'The patient has an invalid IHI number, Please check it and try again.',
        status: 'error',
        isClosable: true,
      })
      return
    }
    if (!isMedicareAcceptable) {
      data.medicare_no = ''
      data.medicare_irn = ''
    }
    if (!isDvaAcceptable) {
      data.dva_file_no = ''
      data.dva_card_colour = ''
    }

    const updatePayload = {
      ...data,
      ...newRecord,
    }

    const result = await updatePatient({
      prescriberId: effectivePrescriberId,
      patient: updatePayload,
      patientId: patient.id,
    })
    if ('data' in result) {
      reset(data)
      onClose()
      resetStore()
    }
  }
  const onPatientDelete = async () => {
    if (!isSharedPatientsFeatureOn && !effectivePrescriberId) {
      toast({
        title: 'Cannot delete patient',
        description: getMissingPrescriberMessage('deleting a patient'),
        status: 'error',
        isClosable: true,
      })
      return
    }

    try {
      await deletePatient({
        prescriberId: effectivePrescriberId,
        patientId: patient.id,
      })
      reset()
      onClose()
      resetStore()
    } catch (error) {
      console.error(error)
    }
  }

  const resetStore = () => {
    dispatch(resetMedicareValidationResult())
    dispatch(resetDvaValidationResult())
    dispatch(resetIhiValidationResult())
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset()
        onClose()
        resetStore()
      }}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={handleSubmit(onPatientUpdate)}
        width="100%"
        maxWidth="1000px"
      >
        <ModalHeader>Update Patient</ModalHeader>
        {updatePatientError?.error === 'duplicated-ihi-number' && (
          <Alert status="error" ref={errorRef}>
            <AlertIcon />
            <AlertDescription
              fontSize={14}
            >{`Sorry, failed to update patient, and the error is ${updatePatientError.message}`}</AlertDescription>
          </Alert>
        )}
        <ModalCloseButton />
        <ModalBody>
          <FormProvider {...methods}>
            <PatientForm
              defaultPatient={patient}
              selectedPrescriber={effectivePrescriber}
              hidePrescriberSelection={true}
              isSharedPatientsFeatureOn={isSharedPatientsFeatureOn}
            />
          </FormProvider>
        </ModalBody>

        <ModalFooter justifyContent="space-between">
          <ConfirmationModal
            title="Delete Patient"
            body="Are you sure you want to delete this patient?"
            onConfirm={() => {
              onPatientDelete()
            }}
          >
            {({ openModal }) => (
              <Button
                colorScheme="red"
                onClick={openModal}
                disabled={!isAbleToCreateOrDelete}
                isLoading={deletePatientResult.isLoading}
              >
                Delete
              </Button>
            )}
          </ConfirmationModal>
          <Button
            colorScheme="blue"
            type="submit"
            disabled={!isAbleToCreateOrDelete}
            isLoading={updatePatientResult.isLoading}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default UpdatePatientModal
